import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Download, Plus, KeyRound } from 'lucide-react';
import { exportCsv } from '../../utils/exportCsv';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import SearchInput from '../../components/common/SearchInput';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import EmptyState from '../../components/common/EmptyState';
import Avatar from '../../components/common/Avatar';
import { adminService } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import UserFormModal from './UserFormModal';
import { formatRole } from '../../utils/enums';

const PAGE_SIZE = 20;

const AdminUsers = () => {
  const { error, success } = useToast();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ role: '', status: '', q: '', active: '' });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.listUsers({ ...filter, page, limit: PAGE_SIZE });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, page, error]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (userId, status, active = true) => {
    try {
      await adminService.updateUserStatus(userId, { status, active });
      success(`Usuário ${status === 'active' ? 'ativado' : 'suspenso'}`);
      load();
    } catch (e) {
      error(e.message);
    }
  };

  const sendReset = async (userId) => {
    try {
      await adminService.resetUserPassword(userId);
      success('Email de reset enviado');
    } catch (e) {
      error(e.message);
    }
  };

  const columns = [
    {
      key: 'user',
      header: 'Usuário',
      render: (u) => (
        <div className="flex items-center gap-3">
          <Avatar name={u.name} size="sm" />
          <div className="min-w-0">
            <p className="font-semibold text-app-text truncate">{u.name}</p>
            <p className="text-xs text-app-text-secondary truncate">{u.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Papel',
      render: (u) => (
        <Badge color="neutral" variant="soft" size="sm">
          {formatRole(u.role)}
        </Badge>
      ),
    },
    {
      key: 'memberships',
      header: 'Lojas',
      render: (u) => {
        const ms = u.memberships || [];
        if (ms.length === 0) return <span className="text-xs text-app-text-secondary">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {ms.slice(0, 3).map((m) => (
              <Badge key={`${u.id}-${m.storeId}-${m.roleInStore}`} color="info" variant="soft" size="sm">
                {m.slug}/{m.roleInStore}
              </Badge>
            ))}
            {ms.length > 3 && <span className="text-xs text-app-text-secondary">+{ms.length - 3}</span>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <div className="flex items-center gap-2">
          <StatusBadge domain="user" status={u.status} />
          {!u.active && (
            <Badge color="error" size="sm" variant="soft">
              Inativo
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Telefone',
      render: (u) => <span className="text-sm text-app-text-secondary">{u.phone || '—'}</span>,
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (u) => (
        <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" leftIcon={<KeyRound size={14} />} onClick={() => sendReset(u.id)}>
            Reset
          </Button>
          {u.status !== 'active' && (
            <Button size="sm" variant="subtle" onClick={() => changeStatus(u.id, 'active', true)}>
              Ativar
            </Button>
          )}
          {u.status !== 'suspended' && (
            <Button size="sm" variant="danger" onClick={() => changeStatus(u.id, 'suspended', false)}>
              Suspender
            </Button>
          )}
        </div>
      ),
    },
  ];

  const clearFilters = () => {
    setFilter({ role: '', status: '', q: '', active: '' });
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold text-app-text">Usuários</h1>
        <div className="flex gap-2">
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            Novo usuário
          </Button>
          <Button
            variant="subtle"
            leftIcon={<Download size={16} />}
            onClick={() =>
              exportCsv({
                filename: 'usuarios.csv',
                columns: [
                  { key: 'id', header: 'ID' },
                  { key: 'name', header: 'Nome' },
                  { key: 'email', header: 'Email' },
                  { key: 'role', header: 'Papel', format: (r) => formatRole(r) },
                  { key: 'status', header: 'Status' },
                  { key: 'active', header: 'Ativo', format: (v) => (v ? 'Sim' : 'Não') },
                  { key: 'phone', header: 'Telefone' },
                ],
                rows,
              })
            }
            disabled={!rows.length}
          >
            Exportar CSV
          </Button>
        </div>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <SearchInput
              value={filter.q}
              onChange={(q) => {
                setFilter((f) => ({ ...f, q }));
                setPage(1);
              }}
              placeholder="Buscar nome, email ou CPF…"
              debounceMs={300}
            />
          </div>
          <Select
            value={filter.role}
            onChange={(e) => {
              setFilter((f) => ({ ...f, role: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos papéis' },
              { value: 'platform_admin', label: 'Admin Reviter' },
              { value: 'store_owner', label: 'Manager' },
              { value: 'reseller', label: 'Revendedor' },
              { value: 'client', label: 'Cliente' },
            ]}
          />
          <Select
            value={filter.status}
            onChange={(e) => {
              setFilter((f) => ({ ...f, status: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'active', label: 'Ativo' },
              { value: 'suspended', label: 'Suspenso' },
              { value: 'pending', label: 'Pendente' },
            ]}
          />
          <Select
            value={filter.active}
            onChange={(e) => {
              setFilter((f) => ({ ...f, active: e.target.value }));
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos (ativos+inativos)' },
              { value: 'true', label: 'Apenas ativos' },
              { value: 'false', label: 'Apenas inativos' },
            ]}
          />
        </div>
        {(filter.q || filter.role || filter.status || filter.active) && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpar filtros
            </Button>
          </div>
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onRowClick={(u) => navigate(`/admin-reviter/usuarios/${u.id}`)}
        selectable
        renderBulkActions={(ids, clear) => (
          <>
            <Button
              size="sm"
              variant="subtle"
              onClick={async () => {
                await Promise.all(
                  ids.map((id) => adminService.updateUserStatus(id, { status: 'active', active: true }))
                );
                success(`${ids.length} usuários ativados`);
                clear();
                load();
              }}
            >
              Ativar selecionados
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={async () => {
                await Promise.all(
                  ids.map((id) => adminService.updateUserStatus(id, { status: 'suspended', active: false }))
                );
                success(`${ids.length} usuários suspensos`);
                clear();
                load();
              }}
            >
              Suspender selecionados
            </Button>
          </>
        )}
        empty={
          <EmptyState
            icon={Users}
            title="Nenhum usuário encontrado"
            description="Ajuste os filtros pra ver mais resultados."
          />
        }
      />

      <UserFormModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        user={null}
        onSaved={load}
      />
    </div>
  );
};

export default AdminUsers;
